import { useState } from "react";
import DOMPurify from "dompurify";
import css from "./ClientBuilderPanel.module.css";
import { resolveTranslatableValue } from "./translatable.util";
import { resolveIconComponent } from "./iconLibrary.constants";
import { CardIconGlyph } from "./CardIconGlyph";
import { HERO_IMAGE_ADMIN_PREVIEW_PATHS } from "./heroLayouts.constants";
import { QUIZ_IMAGE_ADMIN_PREVIEW_PATHS } from "./quizLayouts.constants";
import { NAVBAR_LOGO_ADMIN_PREVIEW_PATHS } from "./navbarLayouts.constants";
import { resolveSocialPlatformIcon } from "./socialPlatforms.constants";
import { resolveWrapStyle } from "./wrapControl.constants";

const brandCode = process.env.REACT_APP_PROJECT_CODE || "CK";

// ── Non-hero components kept brand-specific for now (unchanged behavior) ──
// featuredMeals is commented out of COMPONENT_REGISTRY — out of scope
// beyond hero for this feature (see plan.md scope). Its per-brand values
// are preserved via a brand-keyed lookup so nothing is silently dropped.
// navbar/footer's equivalent brand-keyed constants/preview components were
// here too, but are superseded by navbarLayouts.constants.js/
// footerLayouts.constants.js (Step 8) + COMPONENT_REGISTRY's layouts-based
// entries (Step 9) — deleted, not just commented out, since they're no
// longer read anywhere.

const FEATURED_MEALS_DEFAULTS_BY_BRAND = {
    MEELZ: { sectionTitle: "This week's picks",       accentColor: "#c9a96e", cardBg: "#ffffff", showBadge: true },
    E4F:   { sectionTitle: "This week's picks",       accentColor: "#27ae60", cardBg: "#ffffff", showBadge: true },
    CK:    { sectionTitle: "Chef's picks this week",  accentColor: "#ff6b35", cardBg: "#ffffff", showBadge: true },
};

const featuredMealsDefaults = FEATURED_MEALS_DEFAULTS_BY_BRAND[brandCode] ?? FEATURED_MEALS_DEFAULTS_BY_BRAND.CK;

// ── Small shared pieces — genuinely identical JSX shape repeated across
// multiple StructuralXPreview functions (or multiple times within one),
// pulled out verbatim (same tag, same children, same style application —
// only the passed-in style VALUES differ per caller, never the structure
// itself, so this can't change what any caller renders). ─────────────────

// Section title heading — used by StructuralOurAdvantagePreview (3x, one per
// displayStyle branch) and StructuralFAQPreview (3x). Both compute their own
// `titleStyle` object (different font size/margin per component) and their
// own resolved title string; this just renders the <h2> itself.
function SectionTitle({ style, children }) {
    return <h2 style={style}>{children}</h2>;
}

// Thank-You page's optional custom note — identical shape across all 3
// displayStyle branches in StructuralThankYouPreview (same font-size/color,
// same wrap-style spread, same conditional-render-if-non-empty guard);
// `marginBottom` is the only value that actually differs between branches
// (16 for "minimal", 12 for the other two), so it's the one prop passed in.
function ThankYouCustomNote({ customNote, wrapStyle, marginBottom }) {
    if (!customNote) return null;
    return <p style={{ fontSize: 11, color: "#888", marginBottom, ...wrapStyle }}>{customNote}</p>;
}

// Thank-You page's "Pay Now" button — byte-identical in the "brand-bold" and
// "card-default" branches (the "minimal" branch has no Pay Now button at
// all, matching its real, already-payment-confirmed copy — not present here
// by omission, never by a hidden/disabled prop).
function ThankYouPayNowButton({ primaryColor }) {
    return (
        <button style={{ background: primaryColor, color: "#fff", border: "none", borderRadius: 10, padding: "10px 20px", fontSize: 13, fontWeight: 600, width: "100%", cursor: "pointer", marginBottom: 8 }}>
            Pay Now
        </button>
    );
}

// Thank-You page's "Go to Dashboard" button — byte-identical across all 3
// displayStyle branches.
function ThankYouDashboardButton({ primaryColor }) {
    return (
        <button style={{ background: "transparent", border: `2px solid ${primaryColor}`, color: primaryColor, borderRadius: 10, padding: "10px 20px", fontSize: 13, fontWeight: 600, width: "100%", cursor: "pointer" }}>
            Go to Dashboard
        </button>
    );
}

// ── Inline preview renderers ──────────────────────────────────

// One structural renderer for every hero layout — reads arrangement/
// alignment/colors/text/image and lays itself out accordingly. Replaces
// HeroPreviewMeelz/HeroPreviewE4F/HeroPreviewCK entirely (see plan.md
// "Critical architectural correction"). Same rendering logic the client-side
// StructuralHero (Step 8) uses.
// Step 11: renders hero's features grid — a brand-new component, not a
// retrofit of existing code, so the malformed-entry skip-rule is
// implemented correctly from the very first version: `label` is resolved
// via resolveTranslatableValue BEFORE the emptiness check, never checking a
// raw per-language object's truthiness directly. Absent/empty `features`
// renders nothing (matches the client-side rule from plan.md Step 12).
function StructuralFeaturesGrid({ features, columns, iconPosition, primaryColor, iconColor, textColor, lang }) {
    const visibleFeatures = (features || []).filter((f) => resolveTranslatableValue(f.label, lang));
    if (visibleFeatures.length === 0) return null;

    return (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns === 1 ? 1 : 2}, 1fr)`, gap: 12, width: "100%" }}>
            {visibleFeatures.map((f, i) => {
                const Icon = resolveIconComponent(f.icon);
                return (
                    <div key={i} style={{
                        display: "flex",
                        flexDirection: iconPosition === "top" ? "column" : "row",
                        alignItems: iconPosition === "top" ? "flex-start" : "center",
                        gap: 10,
                    }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: (iconColor || primaryColor) + "1a", color: iconColor || primaryColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                            {Icon ? <Icon /> : "★"}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 500, color: textColor || "#141414", ...resolveWrapStyle(f.labelWrap) }}>{resolveTranslatableValue(f.label, lang)}</span>
                    </div>
                );
            })}
        </div>
    );
}

// MEELZ-only standalone "Features Grid" component — matches the REAL,
// separate, always-live <Features /> component (meelz-client-v2's
// src/components/features.tsx), rendered directly below the hero section,
// never inside the hero card. Distinct from StructuralFeaturesGrid above
// (which stays as hero's own nested sub-field for E4F/CK, unchanged) —
// this renderer matches features.tsx's real layout: a centered, wrapping
// flex row of icon+label pairs, not a fixed-column grid.
function StructuralFeaturesGridPreview({ p, lang }) {
    const { backgroundColor = "#FFFBF4", primaryColor = "#573121", items = [] } = p;
    const visibleItems = items.filter((item) => resolveTranslatableValue(item.label, lang));

    return (
        <div style={{ background: backgroundColor, padding: "32px 24px", borderRadius: 12, width: "100%" }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 24 }}>
                {visibleItems.map((item, i) => {
                    const Icon = resolveIconComponent(item.icon);
                    return (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ color: primaryColor, fontSize: 20, display: "flex" }}>
                                {Icon ? <Icon /> : "★"}
                            </span>
                            <span style={{ fontSize: 14, color: "#333", ...resolveWrapStyle(item.labelWrap) }}>{resolveTranslatableValue(item.label, lang)}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// Reproduces each real client repo's actual Tailwind breakpoints for hero,
// audited directly from structural-hero.tsx (ck-v2/e4f-v2 — byte-identical)
// and structuralHero.tsx (meelz-client-v2, which genuinely differs). Real
// breakpoints: sm=640, md=768, lg=1024 (Tailwind v4 defaults, no overrides
// in any of the 3 repos). "Tablet" (previewSize "medium", 768px) sits
// exactly at sm/md but below lg; "Laptop" ("large", 1280px) is comfortably
// above lg — the one breakpoint every real hero's big layout change (1-col
// -> 2-col, image visibility) happens at.
//
// brandCode (this admin build's own env var) is the correct signal for
// WHICH set of breakpoint values applies — unlike the `p.features` check
// below (which must stay data-driven so a cross-brand layout pick doesn't
// desync it), responsive CSS behavior comes from whichever repo's own
// component actually renders this on publish, i.e. this build's brand, not
// which layout preset is currently selected in the dropdown.
//
// Only the properties actually confirmed responsive in the real components
// are varied here — everything else (tagline size for CK/E4F, overall
// gap, etc.) has no responsive class in the real code, so it stays fixed
// across all three preview sizes exactly as before this change.
function StructuralHeroPreview({ p, lang, previewSize = "large" }) {
    const isColumn = p.arrangement === "column";
    const textAlign = p.alignment === "center" ? "center" : "left";
    // Step 10 (revised): imageSource "upload" means `image` is a real,
    // permanent GCS URL (uploaded via siteConfigService.uploadImage, same
    // bucket every other admin upload uses) — rendered directly; otherwise
    // (including old rows with no imageSource at all — mergeEntry backfills
    // those to "library") fall back to the existing library-id lookup.
    const imageSrc = (p.imageSource === "upload" && p.image)
        ? p.image
        : HERO_IMAGE_ADMIN_PREVIEW_PATHS[p.image];

    const isMeelz = brandCode === "MEELZ";
    const isTabletUp = previewSize !== "small";   // >= sm(640)/md(768)
    const isDesktopUp = previewSize === "large";  // >= lg(1024)

    const cardMinHeight = isMeelz
        ? (isDesktopUp ? 570 : 500)                                 // h-[500px] lg:h-[570px]
        : (isDesktopUp ? 650 : isTabletUp ? 600 : 500);              // min-h-[500px] sm:min-h-[600px] lg:min-h-[650px]
    const gridPadding = isMeelz
        ? "24px 16px"                                                // no audited responsive content-padding class for MEELZ
        : (isDesktopUp ? "96px 64px" : isTabletUp ? "80px 48px" : "64px 32px"); // px-8 sm:px-12 lg:px-16 py-16 sm:py-20 lg:py-24
    const headlineSize = isMeelz
        ? (isDesktopUp ? 48 : 30)                                    // text-3xl lg:text-5xl
        : (isDesktopUp ? 72 : isTabletUp ? 64 : 42);                 // text-[42px] sm:text-[56px] md:text-[64px] lg:text-[72px]
    const descriptionSize = isMeelz
        ? (isDesktopUp ? 16 : 14)                                    // text-sm lg:text-base
        : (isDesktopUp ? 22 : isTabletUp ? 20 : 18);                 // text-[18px] sm:text-[20px] lg:text-[22px]
    const taglineSize = isMeelz ? (isDesktopUp ? 16 : 14) : 12;      // MEELZ: text-sm lg:text-base; CK/E4F has no responsive tagline class
    const contentGap = isMeelz ? (isDesktopUp ? 20 : 8) : 16;        // MEELZ: space-y-2 lg:space-y-5; CK/E4F has no responsive gap class
    const contentPaddingLeft = isMeelz && isDesktopUp ? 40 : 0;      // MEELZ: pl-0 lg:pl-10
    const ctaPadding = isMeelz
        ? (isDesktopUp ? "12px 28px" : "8px 16px")                   // py-2 lg:py-3 px-4 lg:px-7
        : (isTabletUp ? "20px 40px" : "16px 32px");                  // px-8 sm:px-10 py-4 sm:py-5
    const ctaFontSize = isMeelz ? undefined : (isTabletUp ? 18 : 16); // text-[16px] sm:text-[18px] (MEELZ's CTA text size isn't in the audited classes — left as-is)
    const ctaAlign = isMeelz ? (isDesktopUp ? "flex-start" : "center") // mx-auto lg:mx-0
        : (textAlign === "center" ? "center" : "flex-start");
    // MEELZ's features grid breaks at sm(640), not lg(1024) — confirmed a
    // genuinely different threshold from every other property above.
    const featuresColumns = isMeelz ? (isTabletUp ? Math.max(p.featuresColumns || 2, 2) : 1) : p.featuresColumns;

    // Real hero image visibility: CK/E4F's "flat" style and BOTH of MEELZ's
    // styles are hidden entirely below lg (no image on mobile/tablet, not a
    // smaller one) — `hidden lg:flex`/`hidden lg:block`. CK/E4F's "boxed"
    // style always shows, only reordering above the text below lg
    // (`order-first lg:order-last`).
    const showImage = (p.imageStyle === "flat" || isMeelz) ? isDesktopUp : true;
    const boxedImageOrder = p.imageStyle === "boxed" && !isMeelz && !isDesktopUp ? -1 : 0;

    return (
        <div style={{ background: p.backgroundColor, width: "100%", borderRadius: 16, overflow: "hidden", minHeight: cardMinHeight }}>
            <div style={{ display: "grid", gridTemplateColumns: isColumn ? "1fr" : (isDesktopUp ? "1fr 1fr" : "1fr"), gap: isDesktopUp ? 32 : 20, padding: gridPadding, alignItems: "center" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: contentGap, alignItems: textAlign === "center" ? "center" : "flex-start", textAlign, paddingLeft: contentPaddingLeft }}>
                    <span style={{ display: "inline-block", background: p.primaryColor + "22", color: p.primaryColor, fontSize: taglineSize, fontWeight: 600, padding: "4px 12px", borderRadius: 999, width: "fit-content", ...resolveWrapStyle(p.taglineWrap) }}>
                        {resolveTranslatableValue(p.tagline, lang)}
                    </span>
                    <h1 style={{ fontSize: headlineSize, fontWeight: 700, color: p.textColor || "#141414", margin: 0, lineHeight: 1.2, fontFamily: "Poppins, sans-serif", ...resolveWrapStyle(p.headlineWrap) }}>
                        {resolveTranslatableValue(p.headline, lang)}
                    </h1>
                    <p style={{ fontSize: descriptionSize, color: p.textColor ? `${p.textColor}cc` : "#555", lineHeight: 1.6, margin: 0, ...resolveWrapStyle(p.descriptionWrap) }}>
                        {resolveTranslatableValue(p.description, lang)}
                    </p>
                    <button style={{ background: p.ctaButtonColor || p.primaryColor, color: p.ctaButtonTextColor || "#fff", border: "none", borderRadius: 8, padding: ctaPadding, fontSize: ctaFontSize ?? 14, fontWeight: 600, cursor: "pointer", width: "fit-content", alignSelf: ctaAlign, ...resolveWrapStyle(p.ctaTextWrap) }}>
                        {resolveTranslatableValue(p.ctaText, lang)}
                    </button>
                    {/* Data-driven, not brand-or-layout-driven: MEELZ's own
                        default hero props never include a `features` key at
                        all (heroLayouts.constants.js), so checking p.features
                        directly can never desync from which layout is
                        actually selected — unlike the brandCode checks
                        above, which are about a different question
                        (responsive CSS behavior) that genuinely IS decided
                        by this build's brand, not the selected layout. */}
                    {p.features && p.features.length > 0 && (
                        <StructuralFeaturesGrid
                            features={p.features}
                            columns={featuresColumns}
                            iconPosition={p.featuresIconPosition}
                            primaryColor={p.primaryColor}
                            iconColor={p.featuresIconColor}
                            textColor={p.textColor}
                            lang={lang}
                        />
                    )}
                </div>
                {showImage && (p.imageStyle === "flat" ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <img src={imageSrc} alt="Hero" onError={(e) => { e.target.style.display = "none"; }} style={{ width: "100%", maxHeight: 340, objectFit: "contain" }} />
                    </div>
                ) : (
                    <div style={{ order: boxedImageOrder, position: "relative", borderRadius: 16, overflow: "hidden", minHeight: 300, background: "#e8f5e9" }}>
                        <img src={imageSrc} alt="Hero" onError={(e) => { e.target.style.display = "none"; }} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", minHeight: 280 }} />
                        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.3))" }} />
                    </div>
                ))}
            </div>
        </div>
    );
}

// One structural renderer for every Our Advantage layout — reads `displayStyle`
// to switch between stat-grid / large-stat / icon-grid visual modes.
// All 3 layouts share this single component (same pattern as StructuralHeroPreview).
//
// Column counts below are audited directly from the real components —
// byte-identical across all 3 client repos (structural-our-advantage.tsx in
// ck-v2/e4f-v2, structuralOurAdvantage.tsx in meelz-client-v2), so unlike
// Hero this needs no brandCode branching. Real breakpoints: large-stat is
// `grid-cols-1 sm:grid-cols-2 lg:grid-cols-3`; icon-grid/stat-grid are both
// `grid-cols-1 sm:grid-cols-2 lg:grid-cols-4` (sm=640, lg=1024). Without
// this, the grid always tried to fit every card into one row regardless of
// container width — fine at "Laptop" but a cramped, illegibly narrow mess
// at "Mobile"/"Tablet" (confirmed: e.g. a 4-card row squeezed into 375px).
function StructuralOurAdvantagePreview({ p, lang, previewSize = "large" }) {
    const {
        displayStyle    = "stat-grid",
        sectionTitle    = "",
        backgroundColor = "#f5f5f5",
        primaryColor    = "#333333",
        iconColor,
        textColor,
        cards           = [],
    } = p;

    // Malformed-entry skip-rule: a card missing a non-empty `title` (resolved
    // to its client-facing string first — `title` is translatable, so a raw
    // {en, ru, ar} object's truthiness must never be checked directly, always
    // truthy even when every language is blank) is silently skipped, not
    // rendered blank.
    const visibleCards = cards.filter((card) => resolveTranslatableValue(card.title, lang));

    const isTabletUp = previewSize !== "small";   // >= sm(640)
    const isDesktopUp = previewSize === "large";  // >= lg(1024)
    const maxColsForStyle = displayStyle === "large-stat"
        ? (isDesktopUp ? 3 : isTabletUp ? 2 : 1)
        : (isDesktopUp ? 4 : isTabletUp ? 2 : 1);
    const colCount = Math.min(visibleCards.length || 1, maxColsForStyle);

    const titleStyle = {
        textAlign: "center", fontSize: 20, fontWeight: 700,
        color: textColor || "#141414", margin: "0 0 24px", fontFamily: "Poppins, sans-serif",
        ...resolveWrapStyle(p.sectionTitleWrap),
    };

    if (displayStyle === "large-stat") {
        const cols = colCount;
        return (
            <div style={{ background: backgroundColor, padding: "40px 32px", borderRadius: 12, width: "100%" }}>
                <SectionTitle style={titleStyle}>{resolveTranslatableValue(sectionTitle, lang)}</SectionTitle>
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 24 }}>
                    {visibleCards.map((card, i) => (
                        <div key={i} style={{ textAlign: "center" }}>
                            <div style={{ fontSize: 36, marginBottom: 8, color: iconColor || primaryColor }}><CardIconGlyph icon={card.icon} /></div>
                            <div style={{ fontSize: 52, fontWeight: 200, color: textColor || "#141414", lineHeight: 1, fontFamily: "Poppins, sans-serif" }}>
                                {resolveTranslatableValue(card.big, lang)}
                                <span style={{ fontSize: 18, color: primaryColor, marginLeft: 2 }}>{resolveTranslatableValue(card.unit, lang)}</span>
                            </div>
                            <div style={{ fontWeight: 700, fontSize: 14, marginTop: 12, marginBottom: 5, color: textColor || "#141414", ...resolveWrapStyle(card.titleWrap) }}>{resolveTranslatableValue(card.title, lang)}</div>
                            <div style={{ fontSize: 12, color: textColor ? `${textColor}99` : "#666", lineHeight: 1.5, ...resolveWrapStyle(card.descriptionWrap) }}>{resolveTranslatableValue(card.description, lang)}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (displayStyle === "icon-grid") {
        return (
            <div style={{ background: backgroundColor, padding: "40px 32px", borderRadius: 12, width: "100%" }}>
                <SectionTitle style={titleStyle}>{resolveTranslatableValue(sectionTitle, lang)}</SectionTitle>
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${colCount}, 1fr)`, gap: 14 }}>
                    {visibleCards.map((card, i) => (
                        <div key={i} style={{ background: "#f9f9f9", borderRadius: 10, padding: "22px 14px", textAlign: "center", borderTop: `3px solid ${iconColor || primaryColor}` }}>
                            <div style={{ fontSize: 36, marginBottom: 10, color: iconColor || primaryColor }}><CardIconGlyph icon={card.icon} /></div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: textColor || "#141414", marginBottom: 6, ...resolveWrapStyle(card.titleWrap) }}>{resolveTranslatableValue(card.title, lang)}</div>
                            <div style={{ fontSize: 11, color: textColor ? `${textColor}99` : "#666", lineHeight: 1.5, ...resolveWrapStyle(card.descriptionWrap) }}>{resolveTranslatableValue(card.description, lang)}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // stat-grid (default)
    return (
        <div style={{ background: backgroundColor, padding: "32px 24px", borderRadius: 12, width: "100%" }}>
            <SectionTitle style={titleStyle}>{resolveTranslatableValue(sectionTitle, lang)}</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${colCount}, 1fr)`, gap: 10 }}>
                {visibleCards.map((card, i) => (
                    <div key={i} style={{ background: "#fff", borderRadius: 10, padding: "18px 12px", textAlign: "center", border: `1px solid transparent`, transition: "border-color 0.2s" }}>
                        <div style={{ fontSize: 28, marginBottom: 8, color: iconColor || primaryColor }}><CardIconGlyph icon={card.icon} /></div>
                        <div style={{ borderBottom: "1px solid #e5e7eb", paddingBottom: 8, marginBottom: 10 }}>
                            <span style={{ fontSize: 22, fontWeight: 700, color: textColor || "#141414" }}>{resolveTranslatableValue(card.big, lang)}</span>
                            <span style={{ fontSize: 12, color: "#888", marginLeft: 2 }}>{resolveTranslatableValue(card.unit, lang)}</span>
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 12, color: textColor || "#141414", marginBottom: 5, ...resolveWrapStyle(card.titleWrap) }}>{resolveTranslatableValue(card.title, lang)}</div>
                        <div style={{ fontSize: 11, color: textColor ? `${textColor}99` : "#666", lineHeight: 1.5, ...resolveWrapStyle(card.descriptionWrap) }}>{resolveTranslatableValue(card.description, lang)}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// One structural renderer for every FAQ layout — branches on `displayStyle`
// (tabbed / accordion / grid). Interactive: tabs and accordion items respond
// to clicks so the admin can see the real behaviour in the preview panel.
function StructuralFAQPreview({ p, lang, previewSize = "large" }) {
    const {
        displayStyle    = "tabbed",
        sectionTitle    = "",
        backgroundColor = "#f5f5f5",
        primaryColor    = "#333333",
        textColor,
        iconColor,
        items           = [],
    } = p;

    // Audited from the real structural-faq.tsx/structuralFAQ.tsx (identical
    // across all 3 client repos): the "grid" display style is
    // `grid-cols-1 sm:grid-cols-2 lg:grid-cols-3` (sm=640, lg=1024) — a
    // fixed 2-column grid regardless of width looked cramped at Mobile,
    // same class of bug as OurAdvantage's fixed-4-column grid.
    const isTabletUp = previewSize !== "small";
    const isDesktopUp = previewSize === "large";
    const gridCols = isDesktopUp ? 3 : isTabletUp ? 2 : 1;

    const [activeTab,  setActiveTab]  = useState(null);
    const [openItems,  setOpenItems]  = useState({});

    const toggleItem = (key) => setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));

    const titleStyle = {
        textAlign: "center", fontSize: 18, fontWeight: 700, color: textColor || "#141414",
        margin: "0 0 18px", fontFamily: "Poppins, sans-serif",
        ...resolveWrapStyle(p.sectionTitleWrap),
    };

    // Malformed-entry skip-rule: an item missing a non-empty `question`
    // (resolved to its client-facing string first, same reasoning as
    // ourAdvantage's `title` above) is silently skipped, not rendered blank.
    const visibleItems = items.filter((item) => resolveTranslatableValue(item.question, lang));

    if (displayStyle === "tabbed") {
        // Derive unique categories from items. `category` is translatable —
        // grouping/comparison MUST use the resolved string, never the raw
        // {en, ru, ar} object directly: comparing/keying by the raw object
        // would collapse every item into one tab (all objects satisfy the
        // same reference-inequality/stringify behavior identically), a
        // correctness bug distinct from the emptiness-check issue above.
        const categories = [];
        const seen = new Set();
        visibleItems.forEach((item) => {
            const resolvedCategory = resolveTranslatableValue(item.category, lang);
            if (resolvedCategory && !seen.has(resolvedCategory)) {
                seen.add(resolvedCategory);
                categories.push({ id: resolvedCategory, icon: item.icon, label: resolvedCategory, labelWrap: item.categoryWrap });
            }
        });
        const currentTab = activeTab ?? categories[0]?.id;
        const tabItems   = visibleItems.filter((item) => resolveTranslatableValue(item.category, lang) === currentTab);

        return (
            <div style={{ background: backgroundColor, borderRadius: 12, padding: "24px 20px", width: "100%" }}>
                <SectionTitle style={titleStyle}>{resolveTranslatableValue(sectionTitle, lang)}</SectionTitle>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14, justifyContent: "center" }}>
                    {categories.map((cat) => (
                        <button
                            key={cat.id}
                            onClick={() => { setActiveTab(cat.id); setOpenItems({}); }}
                            style={{
                                display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                                padding: "8px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 500,
                                background: currentTab === cat.id ? primaryColor : "#fff",
                                color:      currentTab === cat.id ? "#fff" : "#444",
                            }}
                        >
                            {cat.icon && <span style={{ fontSize: 16, color: currentTab === cat.id ? "#fff" : (iconColor || primaryColor) }}><CardIconGlyph icon={cat.icon} /></span>}
                            <span style={resolveWrapStyle(cat.labelWrap)}>{cat.label}</span>
                        </button>
                    ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {tabItems.map((item, i) => {
                        const key    = `${currentTab}-${i}`;
                        const isOpen = !!openItems[key];
                        return (
                            <div key={key} onClick={() => toggleItem(key)}
                                style={{ background: "#fff", borderRadius: 8, padding: "12px 14px", cursor: "pointer" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: textColor || "#141414", ...resolveWrapStyle(item.questionWrap) }}>{resolveTranslatableValue(item.question, lang)}</span>
                                    <span style={{ fontSize: 10, color: iconColor || "#aaa", marginLeft: 8 }}>{isOpen ? "▲" : "▼"}</span>
                                </div>
                                {isOpen && <p style={{ fontSize: 12, color: textColor ? `${textColor}99` : "#555", marginTop: 8, lineHeight: 1.5, ...resolveWrapStyle(item.answerWrap) }}>{resolveTranslatableValue(item.answer, lang)}</p>}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (displayStyle === "grid") {
        return (
            <div style={{ background: backgroundColor, borderRadius: 12, padding: "24px 20px", width: "100%" }}>
                <SectionTitle style={titleStyle}>{resolveTranslatableValue(sectionTitle, lang)}</SectionTitle>
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${gridCols}, 1fr)`, gap: 10 }}>
                    {visibleItems.map((item, i) => (
                        <div key={i} style={{ background: "#fff", borderRadius: 8, padding: "14px", borderTop: `3px solid ${iconColor || primaryColor}` }}>
                            {item.icon && <div style={{ fontSize: 20, marginBottom: 6, color: iconColor || primaryColor }}><CardIconGlyph icon={item.icon} /></div>}
                            <div style={{ fontSize: 12, fontWeight: 600, color: textColor || "#141414", marginBottom: 4, ...resolveWrapStyle(item.questionWrap) }}>{resolveTranslatableValue(item.question, lang)}</div>
                            <div style={{ fontSize: 11, color: textColor ? `${textColor}99` : "#666", lineHeight: 1.4, ...resolveWrapStyle(item.answerWrap) }}>{resolveTranslatableValue(item.answer, lang)}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // accordion (default)
    return (
        <div style={{ background: backgroundColor, borderRadius: 12, padding: "24px 20px", width: "100%" }}>
            <SectionTitle style={titleStyle}>{resolveTranslatableValue(sectionTitle, lang)}</SectionTitle>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {visibleItems.map((item, i) => {
                    const isOpen = !!openItems[i];
                    return (
                        <div key={i} onClick={() => toggleItem(i)}
                            style={{ background: "#fff", borderRadius: 8, padding: "12px 14px", cursor: "pointer", borderLeft: `3px solid ${iconColor || primaryColor}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: textColor || "#141414", ...resolveWrapStyle(item.questionWrap) }}>{resolveTranslatableValue(item.question, lang)}</span>
                                <span style={{ fontSize: 10, color: iconColor || "#aaa", marginLeft: 8 }}>{isOpen ? "▲" : "▼"}</span>
                            </div>
                            {isOpen && <p style={{ fontSize: 12, color: textColor ? `${textColor}99` : "#555", marginTop: 8, lineHeight: 1.5, ...resolveWrapStyle(item.answerWrap) }}>{resolveTranslatableValue(item.answer, lang)}</p>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// One structural renderer for every Thank-You page layout — branches on
// `displayStyle` (card-default / minimal / brand-bold). Shows a mock order
// confirmation card using the published colors; all real order data remains
// dynamic (sessionStorage) and is never part of the builder config.
function StructuralThankYouPreview({ p, lang }) {
    // Single visual design now (plan-v4.md Step 3) — the former "card
    // default" look, matching the live page's actual default design.
    // displayStyle/minimal/brand-bold branches are gone; there's nothing
    // left to select between.
    const primaryColor    = p.primaryColor    || "var(--color-primary)";
    const backgroundColor = p.backgroundColor || "#F3F4F6";

    const title               = resolveTranslatableValue(p.title, lang);
    // customNote is optional — resolve to a plain string first, since a raw
    // {en, ru, ar} object is always truthy even when every language is
    // blank, and is never a valid React child directly.
    const customNote = resolveTranslatableValue(p.customNote, lang);
    const customNoteStyle = resolveWrapStyle(p.customNoteWrap);

    // The preview has no real order/payment-method context (unlike the
    // live page, which reads data.paymentMethod from a real order) — Card
    // is shown as the illustrative example. The admin edits all 4 methods'
    // text via PaymentMethodTextsEditor regardless of which one is shown
    // here; the preview isn't required to render all 4 states at once.
    const cardInstructions = resolveTranslatableValue(p.paymentMethodTexts?.Card?.instructions, lang);

    return (
        <div style={{ background: backgroundColor, minHeight: 300, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 12, padding: 24 }}>
            <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", maxWidth: 340, width: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
                <div style={{ background: primaryColor, padding: "28px 24px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                    <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: "#fff", marginBottom: 12 }}>✓</div>
                    <h3 style={{ fontSize: 18, fontWeight: 700, color: p.titleTextColor || "#fff", margin: "0 0 4px" }}>{title}</h3>
                    <p style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", margin: 0 }}>Order #A1B2C3D4</p>
                </div>
                <div style={{ padding: "20px 24px" }}>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>💳 Card payment</div>
                    <div style={{ background: `${primaryColor}0d`, border: `1px solid ${primaryColor}33`, borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
                        <p style={{ fontSize: 12, color: p.textColor || "#555", margin: 0 }}>{cardInstructions}</p>
                    </div>
                    {customNote && <p style={{ fontSize: 11, color: p.textColor ? `${p.textColor}99` : "#888", marginBottom: 12, ...customNoteStyle }}>{customNote}</p>}
                    {/* Button TEXT is intentionally not admin-editable (payNowLabel/
                        goToDashboardLabel removed from THANK_YOU_SCHEMA) — shown here
                        as static illustrative labels matching the live page's i18n
                        fallback strings, not sourced from `p`. */}
                    <button style={{ background: primaryColor, color: p.ctaButtonTextColor || "#fff", border: "none", borderRadius: 10, padding: "10px 20px", fontSize: 13, fontWeight: 600, width: "100%", cursor: "pointer", marginBottom: 8 }}>
                        Pay Now
                    </button>
                    <button style={{ background: "transparent", border: `2px solid ${primaryColor}`, color: primaryColor, borderRadius: 10, padding: "10px 20px", fontSize: 13, fontWeight: 600, width: "100%", cursor: "pointer" }}>
                        Go to dashboard
                    </button>
                </div>
            </div>
        </div>
    );
}

function FeaturedMealsPreview({ p }) {
    const meals = ["Grilled Salmon Bowl", "Chicken Caesar Wrap", "Vegan Power Plate"];
    return (
        <div style={{ background: "#f8f8f8", padding: "48px 40px", width: "100%" }}>
            <h2 style={{ fontSize: 26, fontWeight: 700, color: "#141414", marginBottom: 28 }}>{p.sectionTitle}</h2>
            <div style={{ display: "flex", gap: 16 }}>
                {meals.map((meal, i) => (
                    <div key={i} style={{ flex: 1, background: p.cardBg, borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
                        <div style={{ height: 140, background: `hsl(${i * 40 + 20}, 55%, 80%)`, position: "relative" }}>
                            {p.showBadge && i === 0 && (
                                <span style={{ position: "absolute", top: 10, left: 10, background: p.accentColor, color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 4 }}>NEW</span>
                            )}
                        </div>
                        <div style={{ padding: "14px 16px 18px" }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: "#141414", marginBottom: 4 }}>{meal}</div>
                            <div style={{ color: p.accentColor, fontWeight: 700, fontSize: 15 }}>45.00</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// One structural renderer for every navbar layout — reads bgColor/textColor/
// cta*/alignment/justify/visibility toggles and renders a simplified,
// visually accurate navbar. Does not reproduce full auth-state logic or the
// mobile hamburger drawer (both explicitly dropped from extraction, see
// navbarLayouts.constants.js).
function StructuralNavbarPreview({ p, lang, previewSize = "large" }) {
    const ctaRadius = p.ctaBorderRadius === "pill" ? 999 : p.ctaBorderRadius === "square" ? 0 : 8;
    // ClientBuilderPanel.js's Mobile/Tablet/Laptop toggle already
    // width-constrains this preview's outer container generically for every
    // component; this is the one component-specific behavior layered on top
    // of that: "Tablet" (medium) is exactly Tailwind's default `md`
    // breakpoint (768px), the point at which every client repo's real
    // StructuralNavbar.tsx flips its nav links from `hidden` to `md:flex`.
    // Below that ("Mobile"/small), the real component shows NO nav links and
    // no hamburger replacement (a deliberate simplification documented in
    // navbarLayouts.constants.js) — reproducing that exact disappearance
    // here lets an admin see it happen instead of discovering it only on
    // the live mobile site.
    const showNavLinks = previewSize !== "small";
    const barJustifyMap = { start: "flex-start", "space-between": "space-between", center: "center" };
    const linksAlignMap = { left: "flex-start", center: "center", right: "flex-end", "space-between": "space-between" };
    // logoSource "upload" means `logo` is a real, permanent GCS URL
    // (uploaded via siteConfigService.uploadImage, same as hero's image
    // field) — rendered directly; otherwise (including old rows with no
    // logoSource at all — mergeEntry backfills those to "library") falls
    // back to the existing library-id lookup.
    const logoSrc = (p.logoSource === "upload" && p.logo)
        ? p.logo
        : NAVBAR_LOGO_ADMIN_PREVIEW_PATHS[p.logo];

    // Malformed-entry skip-rule: a link missing a non-empty label OR href
    // (resolved to its client-facing string first, since `label` is
    // translatable) is silently skipped, not rendered blank.
    const visibleLinks = (p.navLinks || []).filter(
        (l) => resolveTranslatableValue(l.label, lang) && resolveTranslatableValue(l.href, lang)
    );

    // MEELZ's real navbar.tsx has a SEPARATE top promo/social bar (its own
    // --color-bg-navbar background) above the main nav bar — distinct from
    // E4F/CK, whose social icons render inline in their one single bar
    // (kept exactly as before via showSocialIcons below). When the top bar
    // is shown, social icons move there instead of the main bar, matching
    // the real navbar.tsx exactly (icons never appear in both places).
    return (
        <div style={{ width: "100%" }}>
            {!showNavLinks && (
                <div style={{ fontSize: 11, color: "#999", textAlign: "center", marginBottom: 6 }}>
                    Nav links hidden below this width on the real site (no hamburger fallback)
                </div>
            )}
            {p.showTopBar && (
                <div style={{ background: p.topBarColor, padding: "8px 28px", borderTopLeftRadius: 16, borderTopRightRadius: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <span style={{ color: p.topBarTextColor || "#ffffff", fontSize: 12, flex: "0 1 60%", textAlign: p.topBarMessageAlignment || "right", ...resolveWrapStyle(p.topBarMessageWrap) }}>{resolveTranslatableValue(p.topBarMessage, lang)}</span>
                    {p.showSocialIcons && (
                        <div style={{ display: "flex", gap: 8 }}>
                            {(p.socialLinks || []).map((link, i) => {
                                const Icon = resolveSocialPlatformIcon(link.platform);
                                return Icon ? <Icon key={i} style={{ color: p.iconColor || p.topBarTextColor || "#ffffff", fontSize: 13 }} /> : null;
                            })}
                        </div>
                    )}
                </div>
            )}
            <div style={{ background: p.bgColor, padding: "14px 28px", borderRadius: p.showTopBar ? "0 0 16px 16px" : (p.barJustify === "center" ? 999 : 16), boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
                {/* CSS Grid (not flex) for "space-between": a flex `flex:1` middle
                    item fights with the outer row's own justify-content, leaving
                    linksAlignment unable to move the links independently of the
                    logo's width. Grid's 3 explicit columns give the links column
                    a fixed track that linksAlignment can position within,
                    completely independent of logo/CTA width. */}
                <div style={p.barJustify === "space-between"
                    ? { display: "grid", gridTemplateColumns: "auto 1fr auto", alignItems: "center", gap: 24 }
                    : { display: "flex", alignItems: "center", justifyContent: barJustifyMap[p.barJustify] || "space-between", gap: 24 }
                }>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: p.logoAlignment === "center" ? "center" : "flex-start" }}>
                        {logoSrc
                            ? <img src={logoSrc} alt="Logo" onError={(e) => { e.target.style.display = "none"; }} style={{ height: 32, maxWidth: 110, objectFit: "contain" }} />
                            : <div style={{ fontWeight: 700, fontSize: 18, color: p.textColor }}>Brand</div>}
                        {/* Real default-navbar.tsx places social icons right
                            after the logo, not near the CTA/search icon —
                            matches that fallback's appearance. Only for the
                            non-top-bar case; showTopBar's own social icons
                            (rendered above, in the top bar) are unaffected. */}
                        {p.showSocialIcons && !p.showTopBar && (
                            <div style={{ display: "flex", gap: 8 }}>
                                {(p.socialLinks || []).map((link, i) => {
                                    const Icon = resolveSocialPlatformIcon(link.platform);
                                    return Icon ? <Icon key={i} style={{ color: p.iconColor || p.textColor, fontSize: 13 }} /> : null;
                                })}
                            </div>
                        )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: linksAlignMap[p.linksAlignment] || "center", gap: 24 }}>
                        {showNavLinks && visibleLinks.map((link, i) => (
                            <span key={i} style={{ color: p.textColor, fontSize: 14, ...resolveWrapStyle(link.labelWrap) }}>{resolveTranslatableValue(link.label, lang)}</span>
                        ))}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        {p.showSearchIcon && <span style={{ color: p.iconColor || p.textColor, fontSize: 16 }}>🔍</span>}
                        <button style={{ background: p.ctaColor, color: p.ctaTextColor || "#fff", padding: "8px 22px", border: "none", borderRadius: ctaRadius, fontSize: 14, fontWeight: 600, cursor: "pointer", ...resolveWrapStyle(p.ctaTextWrap) }}>
                            {resolveTranslatableValue(p.ctaText, lang)}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// One structural renderer for every footer layout — a thin legal bar,
// matching what's actually live today (MEELZ's real siteLegalFooterContent.tsx:
// copyright left, Terms/Privacy links right) or authored to match it (E4F/CK,
// which have no footer of any kind live today — confirmed by direct repo
// read, not assumed). `{year}` in copyrightText is a literal token the
// client-side StructuralFooter substitutes at render time; this admin
// preview substitutes it too, purely for a realistic preview — the stored
// value keeps the literal token, never a baked-in year.
function StructuralFooterPreview({ p, lang }) {
    const {
        bgColor       = "#1C1C1C",
        textColor     = "#ffffff",
        copyrightText = "",
        termsText     = "",
        privacyText   = "",
    } = p;

    const currentYear = new Date().getFullYear();
    const copyrightDisplay = resolveTranslatableValue(copyrightText, lang).replace("{year}", currentYear);

    return (
        <div style={{ background: bgColor, borderRadius: 10, padding: "20px 32px", width: "100%" }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
                <span style={{ color: textColor, fontSize: 14, fontWeight: 600, ...resolveWrapStyle(p.copyrightTextWrap) }}>{copyrightDisplay}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <span style={{ color: textColor, fontSize: 14, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 4, ...resolveWrapStyle(p.termsTextWrap) }}>{resolveTranslatableValue(termsText, lang)}</span>
                    <span style={{ color: textColor, opacity: 0.4 }}>|</span>
                    <span style={{ color: textColor, fontSize: 14, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 4, ...resolveWrapStyle(p.privacyTextWrap) }}>{resolveTranslatableValue(privacyText, lang)}</span>
                </div>
            </div>
        </div>
    );
}

// Quiz section preview (quiz-plan.md Section 5.4) — single layout, no
// displayStyle branches (unlike FAQ). Shows one question + its answers at a
// time, matching the live client's actual one-step-per-screen quiz UI, with
// simple Prev/Next buttons to step through admin-authored questions.
// Resolves a config field to a display string, falling back to a literal
// default when blank — mirrors the real client's `config.field || t("...")`
// fallback pattern (quiz-plan.md Section 10.2) so the admin preview shows
// exactly what a real visitor would see, including the pre-Section-10
// fallback path for rows that have never set these new fields.
function resolveQuizText(value, lang, fallback) {
    return resolveTranslatableValue(value, lang) || fallback;
}

// Quiz section preview (quiz-plan.md Section 5.4, corrected to real fidelity
// in Section 10) — mirrors the real client's wellnessQuiz.tsx +
// takeTheQuiz.tsx structure closely: two-column image+card layout, section
// badge/title/subtitle, tab strip, a real percentage progress bar, one
// question at a time with a selected-answer highlight, a contact-step
// preview, and a toggleable success-screen state — matching FAQ preview's
// established fidelity bar (StructuralFAQPreview above), not the earlier,
// much thinner placeholder this replaces.
function StructuralQuizPreview({ p, lang, previewSize = "large" }) {
    const { questions = [], backgroundColor = "#F9F3E6", primaryColor = "#573121" } = p;
    const [stepIndex, setStepIndex] = useState(0);
    const [selectedAnswerId, setSelectedAnswerId] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);

    // Audited from the real take-the-quiz.tsx (identical CK/E4F/MEELZ
    // pattern): the section image is `lg:col-span-3 hidden lg:block` — fully
    // hidden below lg(1024), not just shrunk — and the answer-options grid
    // is `grid-cols-1 sm:grid-cols-2` (sm=640). Neither responded to
    // container width before this.
    const isTabletUp = previewSize !== "small";  // >= sm(640)
    const isDesktopUp = previewSize === "large"; // >= lg(1024)
    const answerGridCols = isTabletUp ? 2 : 1;

    const visibleQuestions = questions.filter((q) => resolveTranslatableValue(q.question, lang));
    const totalSteps = visibleQuestions.length + 1; // +1 for the fixed contact step
    const onContactStep = stepIndex >= visibleQuestions.length;
    const current = onContactStep ? null : visibleQuestions[stepIndex];
    const pct = Math.round(((stepIndex + 1) / totalSteps) * 100);

    const sectionBadge = resolveQuizText(p.sectionBadge, lang, "Personalised Nutrition");
    const sectionTitle = resolveQuizText(p.sectionTitle, lang, "Take the Quiz");
    const sectionSubtitle = resolveQuizText(p.sectionSubtitle, lang, "Answer a few quick questions and we'll match you with the ideal calorie plan and diet programme for your goals.");
    const takeQuizLabel = resolveQuizText(p.takeQuizLabel, lang, "Take the Quiz");
    const bookConsultationLabel = resolveQuizText(p.bookConsultationLabel, lang, "Book a Consultation");
    const contactStepTitle = resolveQuizText(p.contactStepTitle, lang, "Where would you like us to send your plan?");
    const contactFullNameLabel = resolveQuizText(p.contactFullNameLabel, lang, "Full Name");
    const contactEmailLabel = resolveQuizText(p.contactEmailLabel, lang, "Email Address");
    const contactPhoneLabel = resolveQuizText(p.contactPhoneLabel, lang, "Mobile Number");
    const backButtonLabel = resolveQuizText(p.backButtonLabel, lang, "Back");
    const continueButtonLabel = resolveQuizText(p.continueButtonLabel, lang, "Continue");
    const getMyPlanButtonLabel = resolveQuizText(p.getMyPlanButtonLabel, lang, "Get My Plan");
    const thankYouTitle = resolveQuizText(p.thankYouTitle, lang, "Thank you, {name}! 🎉").replace("{name}", "there");
    const thankYouBodyText = resolveQuizText(p.thankYouBodyText, lang, "We've received your responses. Your personalised wellness plan will be sent to you shortly.");
    const retakeButtonLabel = resolveQuizText(p.retakeButtonLabel, lang, "Retake Quiz");

    const sectionImageSrc = (p.sectionImageSource === "upload" && p.sectionImage)
        ? p.sectionImage
        : QUIZ_IMAGE_ADMIN_PREVIEW_PATHS[p.sectionImage];
    const imageColumn = isDesktopUp ? (
        <div style={{ flex: "0 0 38%", borderRadius: 16, overflow: "hidden", minHeight: 280, background: sectionImageSrc ? `url(${sectionImageSrc}) center/cover` : "#e5e7eb" }} />
    ) : null;

    if (!visibleQuestions.length) {
        return (
            <div style={{ background: "#f5f5f5", borderRadius: 12, padding: "24px 20px", width: "100%", textAlign: "center", color: "#999", fontSize: 12 }}>
                No questions configured yet.
            </div>
        );
    }

    return (
        <div style={{ background: "#fff", borderRadius: 12, padding: "24px 20px", width: "100%" }}>
            <div style={{ textAlign: "center", maxWidth: 480, margin: "0 auto 20px" }}>
                <span style={{ display: "inline-block", background: `${primaryColor}1A`, color: primaryColor, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, padding: "4px 10px", borderRadius: 999, marginBottom: 8 }}>
                    {sectionBadge}
                </span>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: p.textColor || "#141414", margin: "0 0 6px", fontFamily: "Poppins, sans-serif" }}>
                    {sectionTitle}
                </h2>
                <p style={{ fontSize: 12, color: p.textColor ? `${p.textColor}99` : "#666", margin: 0 }}>{sectionSubtitle}</p>
            </div>

            <div style={{ background: backgroundColor, borderRadius: 16, padding: 16 }}>
                <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 16 }}>
                    <button style={{ background: primaryColor, color: "#fff", padding: "6px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600, border: "none" }}>{takeQuizLabel}</button>
                    <button style={{ background: "#fff", color: "#333", padding: "6px 14px", borderRadius: 999, fontSize: 12, fontWeight: 500, border: "1px solid #e5e7eb" }}>{bookConsultationLabel}</button>
                </div>

                <div style={{ display: "flex", gap: 16 }}>
                    {imageColumn}
                    <div style={{ flex: 1, background: "#fff", borderRadius: 16, padding: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                        {showSuccess ? (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 10, minHeight: 220, justifyContent: "center" }}>
                                <div style={{ width: 44, height: 44, borderRadius: "50%", background: primaryColor, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 20 }}>✓</div>
                                <h3 style={{ fontSize: 16, fontWeight: 700, color: "#141414", margin: 0 }}>{thankYouTitle}</h3>
                                <p style={{ fontSize: 12, color: "#666", margin: 0, maxWidth: 260 }}>{thankYouBodyText}</p>
                                <button
                                    onClick={() => { setShowSuccess(false); setStepIndex(0); setSelectedAnswerId(null); }}
                                    style={{ marginTop: 6, padding: "6px 16px", borderRadius: 999, border: "1px solid #e5e7eb", background: "#fff", fontSize: 12, cursor: "pointer" }}
                                >{retakeButtonLabel}</button>
                            </div>
                        ) : (
                            <>
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                        <span style={{ fontSize: 10, color: "#999" }}>Step {stepIndex + 1} of {totalSteps}</span>
                                        <span style={{ fontSize: 10, fontWeight: 600, color: primaryColor }}>{pct}%</span>
                                    </div>
                                    <div style={{ height: 5, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                                        <div style={{ height: "100%", width: `${pct}%`, background: primaryColor, borderRadius: 999 }} />
                                    </div>
                                </div>

                                <div style={{ marginBottom: 12 }}>
                                    <h3 style={{ fontSize: 14, fontWeight: 600, color: p.textColor || "#333", margin: "0 0 2px", display: "flex", alignItems: "center", gap: 6 }}>
                                        {current?.icon && <span style={{ color: p.iconColor || primaryColor }}><CardIconGlyph icon={current.icon} /></span>}
                                        {current ? resolveTranslatableValue(current.question, lang) : contactStepTitle}
                                    </h3>
                                </div>

                                {current ? (
                                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${answerGridCols}, 1fr)`, gap: 8, marginBottom: 12 }}>
                                        {(current.answers || []).map((answer) => {
                                            const isSelected = selectedAnswerId === answer.id;
                                            return (
                                                <button
                                                    key={answer.id}
                                                    onClick={() => setSelectedAnswerId(answer.id)}
                                                    style={{
                                                        display: "flex", alignItems: "center", gap: 8, textAlign: "left",
                                                        padding: "8px 10px", borderRadius: 10, cursor: "pointer",
                                                        border: isSelected ? `2px solid ${primaryColor}` : "2px solid #e5e7eb",
                                                        background: isSelected ? primaryColor : "#fff",
                                                    }}
                                                >
                                                    {answer.emoji && <span style={{ color: isSelected ? "#fff" : (p.iconColor || primaryColor) }}><CardIconGlyph icon={answer.emoji} /></span>}
                                                    <span style={{ display: "flex", flexDirection: "column" }}>
                                                        <span style={{ fontSize: 12, fontWeight: 500, color: isSelected ? "#fff" : (p.textColor || "#333") }}>
                                                            {resolveTranslatableValue(answer.label, lang)}
                                                        </span>
                                                        {resolveTranslatableValue(answer.followUpText, lang) && (
                                                            <span style={{ fontSize: 10, color: isSelected ? "rgba(255,255,255,0.8)" : "#999" }}>
                                                                {resolveTranslatableValue(answer.followUpText, lang)}
                                                            </span>
                                                        )}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                                        {[
                                            { label: contactFullNameLabel, required: p.contactFullNameRequired !== false },
                                            { label: contactEmailLabel, required: p.contactEmailRequired !== false },
                                            { label: contactPhoneLabel, required: p.contactPhoneRequired !== false },
                                        ].filter(({ required }) => required).map(({ label, required }) => (
                                            <div key={label} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                <span style={{ fontSize: 9, fontWeight: 600, color: "#888", textTransform: "uppercase" }}>
                                                    {label}{required ? " *" : " (Optional)"}
                                                </span>
                                                <div style={{ height: 28, border: "2px solid #e5e7eb", borderRadius: 8, background: "#fff" }} />
                                            </div>
                                        ))}
                                        {(p.contactExtraFields || []).map((extraField, i) => (
                                            <div key={extraField.id || i} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                <span style={{ fontSize: 9, fontWeight: 600, color: "#888", textTransform: "uppercase" }}>
                                                    {resolveTranslatableValue(extraField.label, lang) || "Field"}{extraField.required ? " *" : " (Optional)"}
                                                </span>
                                                <div style={{ height: 28, border: "2px solid #e5e7eb", borderRadius: 8, background: "#fff" }} />
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #eee", paddingTop: 10 }}>
                                    <button
                                        onClick={() => { setStepIndex((i) => Math.max(0, i - 1)); setSelectedAnswerId(null); }}
                                        disabled={stepIndex === 0}
                                        style={{ padding: "6px 14px", borderRadius: 999, border: "1px solid #e5e7eb", background: "#fff", fontSize: 11, cursor: stepIndex === 0 ? "not-allowed" : "pointer", opacity: stepIndex === 0 ? 0.5 : 1 }}
                                    >{backButtonLabel}</button>
                                    {onContactStep ? (
                                        <button
                                            onClick={() => setShowSuccess(true)}
                                            style={{ padding: "6px 16px", borderRadius: 999, border: "none", background: primaryColor, color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                                        >{getMyPlanButtonLabel}</button>
                                    ) : (
                                        <button
                                            onClick={() => { setStepIndex((i) => Math.min(totalSteps - 1, i + 1)); setSelectedAnswerId(null); }}
                                            style={{ padding: "6px 16px", borderRadius: 999, border: "none", background: primaryColor, color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                                        >{continueButtonLabel}</button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// Terms/Privacy page previews — simplest form, matching StructuralFooterPreview's
// simplicity (no interactive state, just a styled read of the resolved props).
// Sanitized with the same DOMPurify pass the public client render side uses
// (see meelz-client-v2's structuralTermsPage.tsx/structuralPrivacyPage.tsx) —
// applied here too for consistency, not because the admin's own authored
// content is a different trust boundary.
function StructuralTermsPreview({ p, lang }) {
    const { backgroundColor = "#ffffff", textColor = "#1E1E1E", content = "" } = p;
    const html = DOMPurify.sanitize(resolveTranslatableValue(content, lang));
    return (
        <div style={{ background: backgroundColor, color: textColor, borderRadius: 10, padding: "20px 24px", width: "100%", maxHeight: 420, overflowY: "auto" }}>
            <div className={css.richTextEditorContent} style={{ maxHeight: "none", padding: 0 }} dangerouslySetInnerHTML={{ __html: html }} />
        </div>
    );
}

function StructuralPrivacyPreview({ p, lang }) {
    const { backgroundColor = "#ffffff", textColor = "#1E1E1E", content = "" } = p;
    const html = DOMPurify.sanitize(resolveTranslatableValue(content, lang));
    return (
        <div style={{ background: backgroundColor, color: textColor, borderRadius: 10, padding: "20px 24px", width: "100%", maxHeight: 420, overflowY: "auto" }}>
            <div className={css.richTextEditorContent} style={{ maxHeight: "none", padding: 0 }} dangerouslySetInnerHTML={{ __html: html }} />
        </div>
    );
}

// Client Dashboard theme preview (plan-dashboard-theme.md) — colors-only, no
// `lang`/translatable fields at all (unlike every other preview here), since
// this component has no text content in its schema. Mirrors the real
// dashboard shell's actual structure (meelz-client-v2's dashboardSidebar.tsx/
// mobileBottomNav.tsx): a "Welcome to your dashboard" heading, 5 nav items
// with icons (Dashboard/My Orders/Past Orders/Profile/Devices) — the 2nd item
// ("My Orders") shown pre-selected/active as a representative example, since
// the preview has no real route to be "on" — an "ORDER NOW" promo card
// pinned at the sidebar bottom, a thin top-bar strip, and a content-area
// placeholder. Top bar/footer colors are intentionally NOT shown here since
// they're owned by the existing navbar/footer sections, not this one.
const DASHBOARD_NAV_ITEMS = ["Dashboard", "My Orders", "Past Orders", "Profile", "Devices"];

// Confirmed bug: this preview's inactive-item and "Welcome"/promo-card text
// were hardcoded to fixed dark grays (#555/#8a8a8a) — chosen back when
// sidebarBgColor's only real value was light (e.g. the old #F9F3E6 cream /
// #f7f7f7 white-ish defaults). Once sidebarBgColor is a dark color (e.g.
// E4F's real default, #b11212 red), that same dark gray becomes nearly
// invisible against it — confirmed via a live screenshot showing washed-out
// "Dashboard"/"Past Orders"/"Profile"/"Devices"/"Order Now" text. This
// preview is a separate, hand-rolled implementation from the live client's
// dashboard-sidebar.tsx (which already computes a readable color from the
// actual background), so it needs the identical fix independently — this
// preview and the live client must always render the same way, since an
// admin trusts this preview to show what will actually publish.
function parseHexColor(hexColor) {
    const hex = hexColor.replace("#", "");
    if (hex.length !== 6 && hex.length !== 8) return null;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
    if ([r, g, b, a].some(Number.isNaN)) return null;
    return { r, g, b, a };
}

// `behindHexColor` is required for a translucent color (8-digit hex with an
// alpha channel, e.g. promoCardBgColor's "#ffffff33") — without blending
// against whatever's actually behind it (the sidebar background it sits on
// top of), a low-opacity white reads as fully-opaque white and picks black
// text, when the TRUE rendered color is that white blended at ~20% over a
// dark red sidebar — still fairly dark overall, needing white text, not
// black. Confirmed bug: the "ORDER NOW" preview text came out black instead
// of the correct white once alpha was accounted for naively without
// blending — mirrors the identical fix in the live client's
// dashboard-sidebar.tsx.
function resolveReadableTextColor(hexColor, behindHexColor) {
    if (!hexColor) return "#ffffff";
    const fg = parseHexColor(hexColor);
    if (!fg) return "#ffffff";
    const bg = behindHexColor ? parseHexColor(behindHexColor) : null;
    const behind = bg || { r: 255, g: 255, b: 255, a: 1 };
    const r = fg.r * fg.a + behind.r * (1 - fg.a);
    const g = fg.g * fg.a + behind.g * (1 - fg.a);
    const b = fg.b * fg.a + behind.b * (1 - fg.a);
    const luminance = (r * 299 + g * 587 + b * 114) / 1000;
    return luminance > 200 ? "#1a1a1a" : "#ffffff";
}

function StructuralDashboardThemePreview({ p }) {
    const {
        sidebarBgColor     = "#F9F3E6",
        activeNavBgColor   = "#573121",
        activeNavTextColor = "#ffffff",
        promoCardBgColor   = "#EE86001A",
        accentColor        = "#C4832A",
    } = p;
    const inactiveTextColor = p.sidebarTextColor || resolveReadableTextColor(sidebarBgColor);
    const promoTextColor = p.promoCardTextColor || resolveReadableTextColor(promoCardBgColor, sidebarBgColor);

    return (
        <div style={{ width: "100%", borderRadius: 10, overflow: "hidden", border: "1px solid #eee" }}>
            {/* Top bar strip — placeholder only, this component doesn't own these colors */}
            <div style={{ background: "#fafafa", borderBottom: "1px solid #eee", padding: "10px 16px", fontSize: 12, color: "#999" }}>
                Top bar (colors set in the "Navigation Bar" component)
            </div>
            <div style={{ display: "flex", minHeight: 360 }}>
                {/* Sidebar mockup */}
                <div style={{ width: 220, background: sidebarBgColor, padding: "20px 0", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <div>
                        <div style={{ textAlign: "center", fontSize: 12, color: inactiveTextColor, opacity: 0.7, borderBottom: "1px solid rgba(0,0,0,0.08)", paddingBottom: 16, marginBottom: 16 }}>
                            Welcome to your dashboard
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 12px" }}>
                            {DASHBOARD_NAV_ITEMS.map((label, i) => {
                                const isActive = i === 1;
                                return (
                                    <div
                                        key={label}
                                        style={{
                                            display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 6,
                                            background: isActive ? activeNavBgColor : "transparent",
                                            color: isActive ? activeNavTextColor : inactiveTextColor,
                                            fontSize: 13, fontWeight: isActive ? 600 : 500,
                                        }}
                                    >
                                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: isActive ? activeNavTextColor : inactiveTextColor, flexShrink: 0 }} />
                                        {label}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div style={{ margin: "0 12px", padding: "14px 8px", borderRadius: 6, textAlign: "center", background: promoCardBgColor }}>
                        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: promoTextColor }}>Order Now</div>
                        <div style={{ fontSize: 11, color: promoTextColor, opacity: 0.7 }}>Meal delivery</div>
                    </div>
                </div>
                {/* Content-area placeholder */}
                <div style={{ flex: 1, background: "#ffffff", padding: 24, display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ fontSize: 12, color: "#999" }}>Page content (not editable here — chrome/theme only)</div>
                    <div style={{ flex: 1, borderRadius: 8, background: "#f5f5f5" }} />
                </div>
            </div>
            {/* Mobile accent preview */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderTop: "1px solid #eee", background: "#fafafa" }}>
                <span style={{ width: 36, height: 36, borderRadius: "50%", background: accentColor, display: "inline-block" }} />
                <span style={{ fontSize: 12, color: "#999" }}>Mobile bottom-nav accent button</span>
            </div>
        </div>
    );
}

// Dashboard PAGE-CONTENT theme preview (plan-dashboard-page-theme.md) —
// colors-only, no `lang`. Unlike StructuralDashboardThemePreview (one
// composed shell mockup), this renders 5 SEPARATE small mockups, one per
// dashboard page/route, since these are 5 independent pages an admin
// navigates between — not one composed screen. Each mini-mockup mirrors
// that page's real section structure from the plan's Section 0 audit:
// header+body split only where one genuinely exists (Overview's profile
// card), background-only flat cards/lists everywhere else.
function DashboardPagePreviewLabel({ children }) {
    return <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#8a8a8a", marginBottom: 6 }}>{children}</div>;
}

function StructuralDashboardPagesThemePreview({ p }) {
    const {
        overviewHeaderColor          = "#573121",
        overviewCardBgColor          = "#ffffff",
        overviewProfileTextColor     = "#1E1E1E",
        overviewOrdersTableBgColor   = "#ffffff",
        overviewOrdersTableTextColor = "#1E1E1E",
        activeOrdersBgColor          = "#ffffff",
        activeOrdersTextColor        = "#1E1E1E",
        pastOrdersBgColor            = "#ffffff",
        pastOrdersTextColor          = "#1E1E1E",
        profileBgColor               = "#F9F3E6",
        profileTextColor             = "#1E1E1E",
        devicesBgColor               = "#F9F3E6",
        devicesTextColor             = "#1E1E1E",
    } = p;

    return (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Dashboard overview — the one genuine header+body section */}
            <div>
                <DashboardPagePreviewLabel>Dashboard overview</DashboardPagePreviewLabel>
                <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #eee" }}>
                    <div style={{ background: overviewCardBgColor }}>
                        <div style={{ height: 48, background: overviewHeaderColor }} />
                        <div style={{ padding: "12px 16px", fontSize: 12, color: overviewProfileTextColor }}>Profile card body</div>
                    </div>
                    <div style={{ background: overviewOrdersTableBgColor, borderTop: "1px solid #eee", padding: "10px 16px", fontSize: 12, color: overviewOrdersTableTextColor }}>
                        Orders table
                    </div>
                </div>
            </div>

            {/* My Orders — flat, background only */}
            <div>
                <DashboardPagePreviewLabel>My Orders</DashboardPagePreviewLabel>
                <div style={{ background: activeOrdersBgColor, borderRadius: 10, border: "1px solid #eee", padding: "10px 16px", fontSize: 12, color: activeOrdersTextColor }}>
                    Orders list
                </div>
            </div>

            {/* Past Orders — flat, background only */}
            <div>
                <DashboardPagePreviewLabel>Past Orders</DashboardPagePreviewLabel>
                <div style={{ background: pastOrdersBgColor, borderRadius: 10, border: "1px solid #eee", padding: "10px 16px", fontSize: 12, color: pastOrdersTextColor }}>
                    Orders list
                </div>
            </div>

            {/* Profile — whole page is one section, no card structure */}
            <div>
                <DashboardPagePreviewLabel>Profile</DashboardPagePreviewLabel>
                <div style={{ background: profileBgColor, borderRadius: 10, border: "1px solid #eee", padding: "10px 16px", fontSize: 12, color: profileTextColor }}>
                    Profile form
                </div>
            </div>

            {/* Devices — page-level background only */}
            <div>
                <DashboardPagePreviewLabel>Devices</DashboardPagePreviewLabel>
                <div style={{ background: devicesBgColor, borderRadius: 10, border: "1px solid #eee", padding: "10px 16px", fontSize: 12, color: devicesTextColor }}>
                    Device list
                </div>
            </div>
        </div>
    );
}

export {
    StructuralFeaturesGrid,
    StructuralFeaturesGridPreview,
    StructuralHeroPreview,
    StructuralOurAdvantagePreview,
    StructuralFAQPreview,
    StructuralThankYouPreview,
    StructuralNavbarPreview,
    StructuralFooterPreview,
    StructuralQuizPreview,
    StructuralTermsPreview,
    StructuralPrivacyPreview,
    StructuralDashboardThemePreview,
    StructuralDashboardPagesThemePreview,
    FeaturedMealsPreview,
    FEATURED_MEALS_DEFAULTS_BY_BRAND,
};
